// src/pages/CampaignsPage.jsx - SÜRESİZ KAMPANYA DESTEĞİ EKLENDİ
import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Heart, ShoppingCart, Phone, Mail, MapPin,
  Linkedin, Instagram, Twitter, Tag, Calendar, Package, DollarSign,
  Clock, TrendingUp, Zap, CheckCircle, XCircle,
  Sparkles, Info, ArrowLeft, Infinity
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getAllProducts } from '../services/productService';
import { getAllCampaigns } from '../services/discountService';
import toast from 'react-hot-toast';

export default function CampaignsPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [availableCategories, setAvailableCategories] = useState([]);

  const { cartCount } = useCart();
  const { wishlistCount } = useWishlist();
  const { searchQuery, setSearchQuery } = useSearch();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [campaignsResult, productsResult] = await Promise.all([
          getAllCampaigns(),
          getAllProducts()
        ]);

        if (campaignsResult.success) {
          setCampaigns(campaignsResult.campaigns);
        }
        
        if (productsResult.success) {
          const activeProducts = productsResult.products.filter(product => 
            product.isActive !== false
          );
          setProducts(activeProducts);
          
          const categoriesSet = new Set();
          activeProducts.forEach(product => {
            if (product.category) {
              const mainCat = product.category.includes(' > ')
                ? product.category.split(' > ')[0]
                : product.category;
              categoriesSet.add(mainCat);
            }
          });
          setAvailableCategories(Array.from(categoriesSet));
        }
      } catch (error) {
        console.error('Veri yükleme hatası:', error);
        toast.error('Bağlantı hatası');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  // ✅ Kampanya aktiflik kontrolü - NULL endDate = süresiz
  const isCampaignActive = (campaign) => {
    if (!campaign.endDate || campaign.endDate === 'null' || campaign.endDate === null) {
      return true; // Süresiz kampanya her zaman aktif
    }
    return new Date(campaign.endDate) > new Date();
  };

  const filteredCampaigns = campaigns.filter(campaign => {
    const isActive = isCampaignActive(campaign);
    if (filter === 'active') return isActive;
    if (filter === 'expired') return !isActive;
    return true;
  });

  const activeCampaigns = campaigns.filter(c => isCampaignActive(c));
  const expiredCampaigns = campaigns.filter(c => !isCampaignActive(c));

  const totalActive = activeCampaigns.length;
  const totalOffers = campaigns.length;

  const handleUseOffer = (offer) => {
    setSelectedOffer(offer);
    setShowModal(true);
  };

  return (
    <div className="campaigns-page">
      <Style />

      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
        availableCategories={availableCategories}
        products={products}
      />

      <div className="campaigns-hero">
        <div className="container">
          <h1>🎉 Kampanyalar</h1>
          <p>Tüm kampanyalardan yararlanın</p>
          <div className="auto-apply-info">
            <Info size={18} />
            <span>Tüm kampanyalar sepette otomatik olarak uygulanır</span>
          </div>
        </div>
      </div>

      <main className="main-content">
        <div className="container">
          <div className="campaigns-stats">
            <div className="stat-item">
              <div className="stat-icon active">
                <CheckCircle size={24} />
              </div>
              <div>
                <div className="stat-value">{totalActive}</div>
                <div className="stat-label">Aktif Kampanya</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon campaign-stat">
                <Tag size={24} />
              </div>
              <div>
                <div className="stat-value">{campaigns.length}</div>
                <div className="stat-label">Toplam Kampanya</div>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon total">
                <Sparkles size={24} />
              </div>
              <div>
                <div className="stat-value">{totalOffers}</div>
                <div className="stat-label">Toplam Fırsat</div>
              </div>
            </div>
          </div>

          <div className="filter-section">
            <div className="filter-group">
              <div className="filter-label">Durum:</div>
              <div className="filter-bar">
                <button
                  className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                  onClick={() => setFilter('all')}
                >
                  Tümü ({totalOffers})
                </button>
                <button
                  className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
                  onClick={() => setFilter('active')}
                >
                  Aktif ({totalActive})
                </button>
                <button
                  className={`filter-btn ${filter === 'expired' ? 'active' : ''}`}
                  onClick={() => setFilter('expired')}
                >
                  Sona Eren ({expiredCampaigns.length})
                </button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Kampanyalar yükleniyor...</p>
            </div>
          ) : filteredCampaigns.length === 0 ? (
            <div className="empty-state">
              <Sparkles size={64} color="#E5332A" />
              <h3>Kampanya Bulunamadı</h3>
              <p>Seçili filtreye uygun kampanya bulunmuyor</p>
            </div>
          ) : (
            <div className="offers-list">
              {filteredCampaigns.map(campaign => (
                <CampaignDetailCard
                  key={`campaign-${campaign.id}`}
                  campaign={campaign}
                  onUse={() => handleUseOffer(campaign)}
                  products={products}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {showModal && selectedOffer && (
        <OfferModal
          offer={selectedOffer}
          products={products}
          onClose={() => {
            setShowModal(false);
            setSelectedOffer(null);
          }}
          navigate={navigate}
        />
      )}

      <Footer />
    </div>
  );
}

function OfferModal({ offer, products, onClose, navigate }) {
  const eligibleProducts = products.filter(product => {
    if (offer.targetCategory && offer.targetCategory !== '') {
      return product.category === offer.targetCategory;
    }
    if (offer.targetProducts && offer.targetProducts.length > 0) {
      return offer.targetProducts.includes(product.id);
    }
    return true;
  }).slice(0, 6);

  const handleShopNow = () => {
    onClose();
    if (offer.targetCategory) {
      navigate(`/category/${offer.targetCategory}`);
    } else {
      navigate('/search');
    }
  };

  const handleViewProduct = (productId) => {
    onClose();
    navigate(`/product/${productId}`);
  };

  // ✅ Süre kontrolü
  const isUnlimited = !offer.endDate || offer.endDate === 'null' || offer.endDate === null;

  return (
    <>
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="modal-container">
        <div className="modal-content campaign-modal">
          <div className="modal-header">
            <div className="modal-badge">
              <Tag size={24} />
              <span>KAMPANYA</span>
            </div>
            <button className="modal-close" onClick={onClose} aria-label="Kapat">×</button>
          </div>

          <div className="modal-body">
            <div className="modal-title">
              <div className="campaign-discount-big">%{offer.discountPercent} İNDİRİM</div>
            </div>

            <h2>{offer.title}</h2>
            <p className="modal-description">{offer.description}</p>

            <div className="auto-apply-banner">
              <CheckCircle size={20} />
              <div>
                <div className="banner-title">Otomatik Uygulanır</div>
                <div className="banner-text">
                  Sepetinizde uygun ürünler varsa bu kampanya otomatik olarak uygulanacaktır
                </div>
              </div>
            </div>

            <div className="modal-details">
              <h3>Kampanya Detayları</h3>
              <div className="detail-grid">
                {offer.targetCategory && (
                  <div className="detail-item">
                    <Package size={18} />
                    <div>
                      <div className="detail-label">Geçerli Kategori</div>
                      <div className="detail-value">{offer.targetCategory}</div>
                    </div>
                  </div>
                )}
                {offer.minPurchase > 0 && (
                  <div className="detail-item">
                    <DollarSign size={18} />
                    <div>
                      <div className="detail-label">Minimum Alışveriş</div>
                      <div className="detail-value">{offer.minPurchase}₺</div>
                    </div>
                  </div>
                )}
                {/* ✅ Süre gösterimi */}
                <div className="detail-item">
                  <Calendar size={18} />
                  <div>
                    <div className="detail-label">Kampanya Süresi</div>
                    <div className="detail-value">
                      {isUnlimited ? (
                        <span className="unlimited-badge">
                          <Infinity size={16} /> Süresiz
                        </span>
                      ) : (
                        new Date(offer.endDate).toLocaleDateString('tr-TR')
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {eligibleProducts.length > 0 && (
              <div className="eligible-products">
                <h3>Kampanyadan Yararlanabileceğiniz Ürünler</h3>
                <div className="products-grid-modal">
                  {eligibleProducts.map(product => (
                    <div
                      key={product.id}
                      className="product-mini-card"
                      onClick={() => handleViewProduct(product.id)}
                    >
                      <div className="product-mini-image">
                        <img src={product.image} alt={product.name} />
                      </div>
                      <div className="product-mini-info">
                        <div className="product-mini-name">{product.name}</div>
                        <div className="product-mini-price">{product.currentPrice || product.price}₺</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="modal-actions">
              <button className="modal-btn secondary" onClick={onClose}>
                Kapat
              </button>
              <button className="modal-btn primary" onClick={handleShopNow}>
                <ShoppingCart size={20} />
                Alışverişe Başla
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function CampaignDetailCard({ campaign, onUse, products }) {
  // ✅ Süresiz kampanya kontrolü
  const isUnlimited = !campaign.endDate || campaign.endDate === 'null' || campaign.endDate === null;
  
  const isActive = isUnlimited ? true : new Date(campaign.endDate) > new Date();
  
  const daysLeft = isUnlimited 
    ? null 
    : Math.ceil((new Date(campaign.endDate) - new Date()) / (1000 * 60 * 60 * 24));
  
  const startDate = campaign.startDate && campaign.startDate !== 'null' 
    ? new Date(campaign.startDate).toLocaleDateString('tr-TR')
    : 'Başlangıç tarihi yok';
    
  const endDate = isUnlimited 
    ? 'Süresiz'
    : new Date(campaign.endDate).toLocaleDateString('tr-TR');

  const eligibleProductCount = products.filter(p => {
    if (campaign.targetCategory) return p.category === campaign.targetCategory;
    return true;
  }).length;

  return (
    <div className={`offer-detail-card campaign-card ${!isActive ? 'expired' : ''}`}>
      <div className="offer-type-badge campaign-type-badge">
        <Tag size={16} />
        KAMPANYA
      </div>

      <div className="offer-header">
        <div className="offer-discount-badge campaign-discount">
          <Zap size={24} />
          <span>%{campaign.discountPercent}</span>
        </div>
        <div className={`offer-status ${isActive ? 'active' : 'expired'}`}>
          {isActive ? (
            <>
              <CheckCircle size={16} />
              {isUnlimited ? 'Süresiz' : 'Aktif'}
            </>
          ) : (
            <>
              <XCircle size={16} />
              Süresi Doldu
            </>
          )}
        </div>
      </div>

      <div className="offer-body">
        <h2>{campaign.title}</h2>
        <p className="offer-description">{campaign.description}</p>

        <div className="offer-info-grid">
          <div className="info-item">
            <Calendar size={18} />
            <div>
              <div className="info-label">Başlangıç</div>
              <div className="info-value">{startDate}</div>
            </div>
          </div>

          <div className="info-item">
            <Calendar size={18} />
            <div>
              <div className="info-label">Bitiş</div>
              <div className="info-value">
                {isUnlimited ? (
                  <span className="unlimited-text">
                    <Infinity size={16} /> Süresiz
                  </span>
                ) : (
                  endDate
                )}
              </div>
            </div>
          </div>

          {campaign.targetCategory && (
            <div className="info-item">
              <Package size={18} />
              <div>
                <div className="info-label">Kategori</div>
                <div className="info-value">{campaign.targetCategory}</div>
              </div>
            </div>
          )}

          {campaign.minPurchase > 0 && (
            <div className="info-item">
              <DollarSign size={18} />
              <div>
                <div className="info-label">Min. Alışveriş</div>
                <div className="info-value">{campaign.minPurchase}₺</div>
              </div>
            </div>
          )}

          {/* ✅ Kalan süre sadece sürelilerde göster */}
          {isActive && !isUnlimited && daysLeft !== null && (
            <div className="info-item highlight">
              <Clock size={18} />
              <div>
                <div className="info-label">Kalan Süre</div>
                <div className="info-value">{daysLeft} gün</div>
              </div>
            </div>
          )}

          {eligibleProductCount > 0 && (
            <div className="info-item">
              <TrendingUp size={18} />
              <div>
                <div className="info-label">Uygun Ürün</div>
                <div className="info-value">{eligibleProductCount} ürün</div>
              </div>
            </div>
          )}
        </div>

        {isActive && (
          <button className="use-offer-btn campaign-btn" onClick={onUse}>
            <Tag size={20} />
            Ürünleri Görüntüle
          </button>
        )}
      </div>
    </div>
  );
}

function Style() {
  return <style>{`
    * { 
      margin: 0; 
      padding: 0; 
      box-sizing: border-box; 
      -webkit-tap-highlight-color: transparent;
    }
    html { scroll-behavior: smooth; }
    body { font-family: 'Inter', -apple-system, sans-serif; background: #f8f9fa; color: #2B2B2B; }
    img { max-width: 100%; height: auto; }
    a { text-decoration: none; color: inherit; }
    .campaigns-page { min-height: 100vh; }
    .container { max-width: 1320px; margin: 0 auto; padding: 0 20px; }

    .campaigns-hero { 
      background: linear-gradient(135deg, #E5332A, #ff4436); 
      color: #fff; 
      padding: 96px 0; 
      position: relative;
    }
    
    .campaigns-hero h1 { font-size: 48px; font-weight: 800; margin-bottom: 12px; }
    .campaigns-hero p { font-size: 20px; opacity: 0.9; margin-bottom: 16px; }
    .auto-apply-info { 
      display: inline-flex; 
      align-items: center; 
      gap: 8px; 
      background: rgba(255,255,255,0.2); 
      padding: 10px 16px; 
      border-radius: 10px; 
      font-size: 14px; 
      font-weight: 500; 
    }

    .main-content { padding: 48px 0; }
    .campaigns-stats { 
      display: grid; 
      grid-template-columns: repeat(3, 1fr); 
      gap: 24px; 
      margin-bottom: 40px; 
    }
    .stat-item { 
      background: #fff; 
      padding: 24px; 
      border-radius: 16px; 
      display: flex; 
      align-items: center; 
      gap: 20px; 
      box-shadow: 0 2px 8px rgba(0,0,0,0.04); 
      transition: all 0.3s;
    }
    .stat-item:hover {
      transform: translateY(-4px);
      box-shadow: 0 8px 20px rgba(0,0,0,0.1);
    }
    .stat-icon { 
      width: 64px; 
      height: 64px; 
      border-radius: 16px; 
      display: flex; 
      align-items: center; 
      justify-content: center; 
      color: #fff; 
      flex-shrink: 0;
    }
    .stat-icon.active { background: linear-gradient(135deg, #06d6a0, #04b87c); }
    .stat-icon.campaign-stat { background: linear-gradient(135deg, #E5332A, #ff4436); }
    .stat-icon.total { background: linear-gradient(135deg, #667eea, #764ba2); }
    .stat-value { font-size: 32px; font-weight: 800; color: #2B2B2B; }
    .stat-label { font-size: 14px; color: #868e96; margin-top: 4px; }

    .filter-section { margin-bottom: 32px; }
    .filter-group { margin-bottom: 20px; }
    .filter-label { font-size: 14px; font-weight: 600; color: #2B2B2B; margin-bottom: 12px; }
    .filter-bar { display: flex; gap: 12px; flex-wrap: wrap; }
    .filter-btn { 
      padding: 12px 24px; 
      background: #fff; 
      border: 2px solid #e9ecef; 
      border-radius: 12px; 
      font-weight: 600; 
      cursor: pointer; 
      transition: all 0.2s; 
      display: flex; 
      align-items: center; 
      gap: 8px; 
      font-size: 14px;
    }
    .filter-btn:hover { border-color: #E5332A; color: #E5332A; }
    .filter-btn:active { transform: scale(0.97); }
    .filter-btn.active { background: #E5332A; color: #fff; border-color: #E5332A; }

    .offers-list { display: grid; gap: 24px; }
    .offer-detail-card { 
      background: #fff; 
      border-radius: 20px; 
      padding: 32px; 
      box-shadow: 0 2px 8px rgba(0,0,0,0.04); 
      transition: all 0.3s; 
      border: 2px solid #e9ecef; 
      position: relative; 
    }
    .offer-detail-card:hover { 
      transform: translateY(-4px); 
      box-shadow: 0 12px 32px rgba(0,0,0,0.12); 
    }
    .offer-detail-card.campaign-card:hover { border-color: #E5332A; }
    .offer-detail-card.expired { opacity: 0.7; }
    .offer-type-badge { 
      position: absolute; 
      top: 20px; 
      right: 20px; 
      padding: 10px 16px; 
      border-radius: 10px; 
      font-size: 11px; 
      font-weight: 700; 
      display: flex; 
      align-items: center; 
      gap: 6px; 
    }
    .campaign-type-badge { background: rgba(229, 51, 42, 0.1); color: #E5332A; }
    .offer-header { 
      display: flex; 
      justify-content: space-between; 
      align-items: center; 
      margin-bottom: 24px; 
      padding-right: 120px; 
    }
    .offer-discount-badge { 
      display: flex; 
      align-items: center; 
      gap: 12px; 
      color: #fff; 
      padding: 16px 24px; 
      border-radius: 16px; 
      font-size: 24px; 
      font-weight: 800; 
    }
    .campaign-discount { background: linear-gradient(135deg, #E5332A, #ff4436); }
    .offer-status { 
      display: flex; 
      align-items: center; 
      gap: 8px; 
      padding: 10px 20px; 
      border-radius: 10px; 
      font-size: 14px; 
      font-weight: 600; 
    }
    .offer-status.active { background: rgba(6, 214, 160, 0.1); color: #06d6a0; }
    .offer-status.expired { background: rgba(255, 107, 107, 0.1); color: #ff6b6b; }
    .offer-body h2 { font-size: 28px; font-weight: 800; margin-bottom: 12px; color: #2B2B2B; }
    .offer-description { font-size: 16px; color: #868e96; line-height: 1.7; margin-bottom: 32px; }
    .offer-info-grid { 
      display: grid; 
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
      gap: 20px; 
      margin-bottom: 24px; 
    }
    .info-item { 
      display: flex; 
      align-items: center; 
      gap: 12px; 
      padding: 16px; 
      background: #f8f9fa; 
      border-radius: 12px; 
    }
    .info-item.highlight { background: linear-gradient(135deg, #fff5f5, #ffe8e8); }
    .info-item svg { flex-shrink: 0; color: #868e96; }
    .info-label { font-size: 12px; color: #868e96; margin-bottom: 4px; }
    .info-value { font-size: 16px; font-weight: 700; color: #2B2B2B; }
    
    /* ✅ Süresiz badge stil */
    .unlimited-text,
    .unlimited-badge {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      color: #06d6a0;
      font-weight: 700;
    }
    
    .use-offer-btn { 
      width: 100%; 
      padding: 16px; 
      color: #fff; 
      border: none; 
      border-radius: 12px; 
      font-size: 16px; 
      font-weight: 700; 
      cursor: pointer; 
      transition: all 0.2s; 
      display: flex; 
      align-items: center; 
      justify-content: center; 
      gap: 10px; 
    }
    .campaign-btn { background: linear-gradient(135deg, #E5332A, #ff4436); }
    .use-offer-btn:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(0,0,0,0.2); }
    .use-offer-btn:active { transform: translateY(0); }

    .modal-overlay { 
      position: fixed; 
      top: 0; 
      left: 0; 
      right: 0; 
      bottom: 0; 
      background: rgba(0,0,0,0.7); 
      z-index: 9998; 
      backdrop-filter: blur(4px); 
      animation: fadeIn 0.2s; 
    }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    .modal-container { 
      position: fixed; 
      top: 50%; 
      left: 50%; 
      transform: translate(-50%, -50%); 
      z-index: 9999; 
      max-width: 800px; 
      width: 90%; 
      max-height: 90vh; 
      overflow-y: auto; 
      animation: slideUp 0.3s; 
    }
    @keyframes slideUp { 
      from { transform: translate(-50%, -40%); opacity: 0; } 
      to { transform: translate(-50%, -50%); opacity: 1; } 
    }
    .modal-content { 
      background: #fff; 
      border-radius: 24px; 
      box-shadow: 0 20px 60px rgba(0,0,0,0.3); 
    }
    .modal-content.campaign-modal { border-top: 4px solid #E5332A; }
    .modal-header { 
      display: flex; 
      justify-content: space-between; 
      align-items: center; 
      padding: 24px 32px; 
      border-bottom: 1px solid #e9ecef; 
    }
    .modal-badge { 
      display: flex; 
      align-items: center; 
      gap: 12px; 
      padding: 10px 20px; 
      border-radius: 10px; 
      font-size: 14px; 
      font-weight: 700; 
    }
    .campaign-modal .modal-badge { background: rgba(229, 51, 42, 0.1); color: #E5332A; }
    .modal-close { 
      background: none; 
      border: none; 
      font-size: 32px; 
      color: #868e96; 
      cursor: pointer; 
      width: 40px; 
      height: 40px; 
      display: flex; 
      align-items: center; 
      justify-content: center; 
      border-radius: 50%; 
      transition: all 0.2s; 
    }
    .modal-close:hover { background: #f8f9fa; color: #2B2B2B; }
    .modal-body { padding: 32px; }
    .modal-title { text-align: center; margin-bottom: 24px; }
    .campaign-discount-big { 
      font-size: 48px; 
      font-weight: 800; 
      background: linear-gradient(135deg, #E5332A, #ff4436); 
      -webkit-background-clip: text; 
      -webkit-text-fill-color: transparent; 
      background-clip: text;
    }
    .modal-body h2 { 
      font-size: 32px; 
      font-weight: 800; 
      text-align: center; 
      margin-bottom: 16px; 
      color: #2B2B2B; 
    }
    .modal-description { 
      text-align: center; 
      font-size: 16px; 
      color: #868e96; 
      margin-bottom: 32px; 
      line-height: 1.7; 
    }
    .auto-apply-banner { 
      display: flex; 
      align-items: flex-start; 
      gap: 16px; 
      background: linear-gradient(135deg, #f0fdf4, #dcfce7); 
      padding: 20px; 
      border-radius: 16px; 
      margin-bottom: 32px; 
      border: 2px solid #06d6a0; 
    }
    .auto-apply-banner svg { flex-shrink: 0; margin-top: 2px; color: #06d6a0; }
    .banner-title { font-size: 16px; font-weight: 700; color: #059669; margin-bottom: 4px; }
    .banner-text { font-size: 14px; color: #059669; line-height: 1.6; }
    .modal-details { margin-bottom: 32px; }
    .modal-details h3 { font-size: 20px; font-weight: 700; margin-bottom: 16px; color: #2B2B2B; }
    .detail-grid { 
      display: grid; 
      grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); 
      gap: 16px; 
    }
    .detail-item { 
      display: flex; 
      align-items: center; 
      gap: 12px; 
      padding: 16px; 
      background: #f8f9fa; 
      border-radius: 12px; 
    }
    .detail-item svg { flex-shrink: 0; color: #868e96; }
    .detail-label { font-size: 12px; color: #868e96; margin-bottom: 4px; }
    .detail-value { font-size: 16px; font-weight: 700; color: #2B2B2B; }
    .eligible-products { margin-bottom: 32px; }
    .eligible-products h3 { font-size: 20px; font-weight: 700; margin-bottom: 16px; color: #2B2B2B; }
    .products-grid-modal { display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px; }
    .product-mini-card { 
      background: #f8f9fa; 
      border-radius: 12px; 
      overflow: hidden; 
      cursor: pointer; 
      transition: all 0.2s; 
      border: 2px solid transparent; 
    }
    .product-mini-card:hover { 
      transform: translateY(-2px); 
      box-shadow: 0 4px 12px rgba(0,0,0,0.1); 
      border-color: #E5332A; 
    }
    .product-mini-image { 
      width: 100%; 
      aspect-ratio: 1; 
      background: #fff; 
      display: flex; 
      align-items: center; 
      justify-content: center; 
      padding: 12px; 
    }
    .product-mini-image img { width: 100%; height: 100%; object-fit: contain; }
    .product-mini-info { padding: 12px; }
    .product-mini-name { 
      font-size: 13px; 
      font-weight: 600; 
      color: #2B2B2B; 
      margin-bottom: 6px; 
      display: -webkit-box; 
      -webkit-line-clamp: 2; 
      -webkit-box-orient: vertical; 
      overflow: hidden; 
    }
    .product-mini-price { font-size: 16px; font-weight: 800; color: #E5332A; }
    .modal-actions { display: flex; gap: 16px; }
    .modal-btn { 
      flex: 1; 
      padding: 16px; 
      border-radius: 12px; 
      font-size: 16px; 
      font-weight: 700; 
      cursor: pointer; 
      transition: all 0.2s; 
      display: flex; 
      align-items: center; 
      justify-content: center; 
      gap: 10px; 
      border: none; 
    }
    .modal-btn.secondary { background: #f8f9fa; color: #2B2B2B; }
    .modal-btn.secondary:hover { background: #e9ecef; }
    .modal-btn.secondary:active { transform: scale(0.97); }
    .modal-btn.primary { background: linear-gradient(135deg, #E5332A, #ff4436); color: #fff; }
    .modal-btn.primary:hover { transform: translateY(-2px); box-shadow: 0 6px 20px rgba(229, 51, 42, 0.3); }
    .modal-btn.primary:active { transform: translateY(0); }

    .loading-state { text-align: center; padding: 80px 20px; }
    .spinner { 
      width: 48px; 
      height: 48px; 
      border: 4px solid #e9ecef; 
      border-top-color: #E5332A; 
      border-radius: 50%; 
      animation: spin 1s linear infinite; 
      margin: 0 auto 16px; 
    }
    @keyframes spin { to { transform: rotate(360deg); } }
    .loading-state p { font-size: 16px; color: #868e96; }
    .empty-state { text-align: center; padding: 80px 20px; }
    .empty-state svg { margin-bottom: 16px; }
    .empty-state h3 { font-size: 24px; font-weight: 800; margin: 16px 0 8px; }
    .empty-state p { color: #868e96; font-size: 16px; }

    /* Responsive Styles */
    @media (max-width: 1200px) {
      .campaigns-stats { grid-template-columns: repeat(2, 1fr); }
      .products-grid-modal { grid-template-columns: repeat(2, 1fr); }
    }
    
    @media (max-width: 992px) {
      .campaigns-hero { padding: 64px 0; }
      .campaigns-hero h1 { font-size: 36px; }
      .campaigns-hero p { font-size: 18px; }
      .offer-info-grid { grid-template-columns: repeat(2, 1fr); }
      .stat-item { padding: 20px; gap: 16px; }
    }
    
    @media (max-width: 768px) {
      .campaigns-hero { padding: 48px 0; }
      .campaigns-hero h1 { font-size: 28px; }
      .campaigns-hero p { font-size: 16px; }
      .auto-apply-info { font-size: 12px; padding: 8px 12px; }
      .auto-apply-info svg { width: 16px; height: 16px; }
      
      .main-content { padding: 32px 0; }
      .campaigns-stats { grid-template-columns: 1fr; gap: 16px; }
      .stat-item { padding: 16px; gap: 12px; }
      .stat-icon { width: 48px; height: 48px; }
      .stat-icon svg { width: 20px; height: 20px; }
      .stat-value { font-size: 24px; }
      .stat-label { font-size: 13px; }
      
      .filter-section { margin-bottom: 24px; }
      .filter-label { font-size: 13px; }
      .filter-btn { padding: 10px 16px; font-size: 13px; }
      
      .offer-detail-card { padding: 20px; border-radius: 16px; }
      .offer-header { 
        flex-direction: column; 
        align-items: flex-start; 
        gap: 12px; 
        padding-right: 0; 
        margin-bottom: 20px;
      }
      .offer-type-badge { 
        position: static; 
        margin-bottom: 8px; 
        font-size: 10px;
        padding: 8px 12px;
      }
      .offer-discount-badge { 
        font-size: 20px; 
        padding: 12px 20px; 
      }
      .offer-discount-badge svg { width: 20px; height: 20px; }
      .offer-status { font-size: 13px; padding: 8px 16px; }
      .offer-body h2 { font-size: 20px; }
      .offer-description { font-size: 14px; margin-bottom: 24px; }
      .offer-info-grid { grid-template-columns: 1fr; gap: 12px; }
      .info-item { padding: 12px; }
      .info-item svg { width: 16px; height: 16px; }
      .info-label { font-size: 11px; }
      .info-value { font-size: 14px; }
      .use-offer-btn { font-size: 14px; padding: 14px; }
      .use-offer-btn svg { width: 18px; height: 18px; }
      
      .modal-container { width: 95%; max-height: 85vh; }
      .modal-header { padding: 20px; }
      .modal-badge { font-size: 12px; padding: 8px 16px; }
      .modal-badge svg { width: 18px; height: 18px; }
      .modal-close { font-size: 28px; width: 36px; height: 36px; }
      .modal-body { padding: 20px; }
      .campaign-discount-big { font-size: 36px; }
      .modal-body h2 { font-size: 24px; }
      .modal-description { font-size: 14px; margin-bottom: 24px; }
      .auto-apply-banner { padding: 16px; gap: 12px; }
      .auto-apply-banner svg { width: 18px; height: 18px; }
      .banner-title { font-size: 14px; }
      .banner-text { font-size: 13px; }
      .modal-details h3 { font-size: 18px; }
      .detail-grid { grid-template-columns: 1fr; gap: 12px; }
      .detail-item { padding: 12px; }
      .eligible-products h3 { font-size: 18px; }
      .products-grid-modal { grid-template-columns: repeat(2, 1fr); gap: 12px; }
      .product-mini-name { font-size: 12px; }
      .product-mini-price { font-size: 14px; }
      .modal-actions { flex-direction: column; gap: 12px; }
      .modal-btn { font-size: 14px; padding: 14px; }
      
      .loading-state { padding: 60px 20px; }
      .spinner { width: 40px; height: 40px; }
      .loading-state p { font-size: 14px; }
      .empty-state { padding: 60px 20px; }
      .empty-state svg { width: 48px; height: 48px; }
      .empty-state h3 { font-size: 20px; }
      .empty-state p { font-size: 14px; }
    }
    
    @media (max-width: 576px) {
      .campaigns-hero { padding: 40px 0; }
      .campaigns-hero h1 { font-size: 24px; }
      .campaigns-hero p { font-size: 14px; }
      
      .filter-btn { padding: 8px 12px; font-size: 12px; }
      
      .offer-detail-card { padding: 16px; }
      .offer-discount-badge { font-size: 18px; padding: 10px 16px; }
      .offer-body h2 { font-size: 18px; }
      .offer-description { font-size: 13px; }
      
      .modal-body { padding: 16px; }
      .campaign-discount-big { font-size: 32px; }
      .modal-body h2 { font-size: 20px; }
      .products-grid-modal { grid-template-columns: 1fr; }
    }
    
    @media (max-width: 480px) {
      .campaigns-hero h1 { font-size: 22px; }
      .stat-value { font-size: 20px; }
      .offer-body h2 { font-size: 16px; }
      .campaign-discount-big { font-size: 28px; }
      .modal-body h2 { font-size: 18px; }
    }
  `}</style>;
}